import React, {useState, useCallback, useEffect} from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import DataState from 'app/src/components/DataState';
import Input from 'app/src/elements/inputs';
import { Button, ButtonGroup, ButtonContainer } from 'app/src/elements/buttons';
import { View, Text } from 'react-native';
import { UPDATE_EXPENSE } from './queries';
import { formatAssetNameForSelect } from '../../utils/formatAssetName';
import Autocomplete from '../Autocomplete';
import { useParams } from 'react-router-dom';
import ExpenseLines from './ExpenseLines';
import { PAYMENT_METHODS } from '.';
import {NewAttachments} from '../Attachments/new';
import EditLinkedAttachment from '../Attachments/LinkedAttachments/edit';
import { Link } from 'app/src/utils/routing';
import moment from 'moment';
import elRed from '../../styles/colors';
import { formatDateForInput } from '../../utils/timeFormatter';

const EditExpense = ({setShowModal, refetch, expense, markAsPaidDefault}) => {
  const [submitting, setSubmitting] = useState();
  const [linkedAttachments, setLinkedAttachments] = useState(
    expense?.linkedAttachments.map(attachment => {
      return {
        id: attachment.id,
        name: attachment.name,
        url: attachment.url,
        resourceId: attachment.resourceId,
        resourceType: attachment.resourceType
      }
    })
  );
  const [markAsPaid, setMarkAsPaid] = useState(checkMarkAsPaid());
  const [updateExpense] = useMutation(UPDATE_EXPENSE, {
    onCompleted: () => {
      refetch();
      setShowModal(false);
    }
  });

  const onSubmit = useCallback(input => {
    setSubmitting(true);
    if(markAsPaid) {
      input['markAsPaid'] = true;
    }
    if(input['clearedOn']) {
      input['clearedOn'] = moment(input['clearedOn']).format('YYYY-MM-DD');
    }else {
      input['clearedOn'] = null;
    }
    if(input['linkedAttachments']) {
      input['linkedAttachmentAttributes'] = linkedAttachments;
      delete input['linkedAttachments'];
    }
    updateExpense({ variables: input });
  });

  const { control, handleSubmit, register, getValues, setValue, watch, resetField,
    formState: { errors }
  } = useForm({
    defaultValues: {
      id: expense.id,
      vendorId: expense.vendor?.id || '',
      requestedById: expense.requestedById,
      invoiceNumber: expense.invoiceNumber || '',
      description: expense.description || '',
      paymentMethod: expense.paymentMethod || '',
      paymentReference: expense.paymentReference || '',
      clearedOn: expense.clearedOn ?
        formatDateForInput(expense.clearedOn) : '',
      expenseLines: expense.expenseLines || [],
      expenseFiles: expense.expenseFiles || [],
      linkedAttachments: expense.linkedAttachments || []
    }
  });

  // useEffect(() => {
  //   if(!markAsPaid) {
  //     resetField('paymentMethod', '');
  //     resetField('paymentReference', '');
  //     resetField('clearedOn', '');
  //   }
  // }, [markAsPaid])

  const watchFiles = watch('expenseFiles');

  return (
    <View>
      {submitting ?
        <DataState.Loading /> :
        <View>
          {expense.status === 'Purchase Order' && markAsPaid ?
            <View>
              <Input.Picker
                label="Payment Method:"
                name="paymentMethod"
                options={PAYMENT_METHODS.map(type => {
                  return { value: type, label: type }
                })}
                control={control}
                errors={errors}
              />
              <Input.Text
                label="Payment Reference:"
                name="paymentReference"
                control={control}
                errors={errors}
              />
              <Input.Date
                label="Cleared On:"
                name="clearedOn"
                control={control}
                errors={errors}
              />
            </View> :
            <View>
              <Autocomplete
                label={'Vendor'}
                placeHolder={expense?.vendor?.name}
                resource={'Vendor'}
                setValue={setValue}
                name='vendorId'
                defaultValue={expense?.vendor}
              />
              <Input.Text
                label="Invoice Number:"
                name="invoiceNumber"
                control={control}
                errors={errors}
              />
              <Input.Text
                label="Description:"
                name="description"
                control={control}
                errors={errors}
              />
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  marginLeft: '-2px',
                  marginBottom: '10px'
                }}
              >
                <input
                  type='checkbox'
                  checked={markAsPaid}
                  onChange={() => setMarkAsPaid(!markAsPaid)}
                />
                <Text
                  style={{fontSize: '16px', marginLeft: '5px'}}
                >
                  Mark As Paid?
                </Text>
              </View>
              {markAsPaid &&
                <View>
                  <Input.Picker
                    label="Payment Method:"
                    name="paymentMethod"
                    options={PAYMENT_METHODS.map(type => {
                      return { value: type, label: type }
                    })}
                    control={control}
                    errors={errors}
                  />
                  <Input.Text
                    label="Payment Reference:"
                    name="paymentReference"
                    control={control}
                    errors={errors}
                  />
                  <Input.Date
                    label="Cleared On:"
                    name="clearedOn"
                    control={control}
                    errors={errors}
                  />
                </View>
              }
            </View>
          }
          {checkDisabled() &&
            <View>
              <Text
                style={{
                  color: elRed,
                  fontWeight: 'bold',
                  marginBottom: '10px'
                }}
              >
                Receipt must be uploaded when marking paid.
              </Text>
            </View>
          }
          <Text style={{marginBottom: '-15px', fontSize: '16px'}}>
            Expense Files:
          </Text>
          {/* <br />
            {expense.expenseFiles && expense.expenseFiles.map(file =>
              <View style={{width: 'fit-content'}}>
                <Link to={file.url} target='_blank'>
                  <Text>
                    {file.filename}
                  </Text>
                </Link>
              </View>
            )} */}
          <NewAttachments
            recordId={expense?.id}
            recordType='Expense'
            attachmentType='Expense File'
            refetch={refetch}
            name='expenseFiles'
            setValue={setValue}
            existingFiles={expense?.expenseFiles}
          />
          <View>
            <b>OR</b>
            <EditLinkedAttachment
              linkedAttachments={linkedAttachments}
              resourceId={expense?.id}
              resourceType={'Expense'}
              setValue={setLinkedAttachments}
              multi={true}
            />
          </View>
          <ButtonContainer>
            <ButtonGroup>
              <Button
                label="Update Expense"
                onPress={handleSubmit(onSubmit)}
                disabled={checkDisabled()}
                style={{opacity: checkDisabled() ? 0.5 : 1}}
              />
            </ButtonGroup>
          </ButtonContainer>
          {!markAsPaid &&
            <ExpenseLines
              existingExpenseLines={expense?.expenseLines}
              expense={expense}
              refetch={refetch}
            />
          }
        </View>
      }
    </View>
  )

  function checkDisabled() {
    return markAsPaid && !(watchFiles?.length || linkedAttachments?.length)
  }

  function checkMarkAsPaid() {
    let check = false;
    if(markAsPaidDefault || (
      expense?.expenseFiles?.length ||
      linkedAttachments?.length
    )) {
      check = true;
    }
    return check;
  }
}

export default EditExpense;
